import { t, type MessageKey } from '../../../i18n';

/**
 * The Long Night's words, explained where they are used: every item's sheet lists the terms its
 * text uses (P, M, Kindled, Epitaph…), so nothing is left unexplained.
 */

interface Term {
  readonly key: string;
  /** How the term shows in item texts. */
  readonly match: RegExp;
}

const word = (w: string) => new RegExp(`(^|[^A-Za-z])${w}([^A-Za-z]|$)`);

const TERMS: readonly Term[] = [
  { key: 'p', match: /(\d|\s)P\b/ },
  { key: 'm', match: /(\d|×|\s)M\b/ },
  { key: 'brass', match: /¤/ },
  { key: 'chain', match: word('chains?') },
  { key: 'link', match: word('links?') },
  { key: 'landing', match: word('land(s|ing|ings)?') },
  { key: 'capture', match: word('captur(e|es|ed|ing)') },
  { key: 'crown', match: word('crown(s|ed|ing)?') },
  { key: 'pattern', match: word('patterns?') },
  { key: 'ladder', match: word('Ladders?') },
  { key: 'echo', match: word('Echo(es)?') },
  { key: 'streak', match: word('Streaks?') },
  { key: 'stair', match: word('Stair') },
  { key: 'prism', match: word('Prism') },
  { key: 'mirror', match: word('Mirror') },
  { key: 'fullCircle', match: /Full Circle/ },
  { key: 'epitaph', match: word('Epitaphs?') },
  { key: 'inscription', match: word('inscri(ption|ptions|bed)') },
  { key: 'wild', match: word('Wilds?') },
  { key: 'collateral', match: word('collateral') },
  { key: 'pawned', match: word('pawn(ed|ing)') },
  { key: 'recover', match: word('Recover(s|ing|ed)?') },
  { key: 'homeRow', match: /home row/ },
  { key: 'round', match: word('rounds?') },
  { key: 'table', match: word('tables?') },
  { key: 'headStart', match: /head start|Ante/ },
  { key: 'mantel', match: word('Mantel') },
  { key: 'superstition', match: word('Superstitions?') },
  { key: 'curio', match: word('Curios?') },
  { key: 'appraisal', match: word('Appraisals?') },
  { key: 'stamp', match: word('Stamps?') },
  { key: 'finish', match: /\b(Finish|Foil|Gilt|Pearl|Ghost)\b/ },
  { key: 'carving', match: word('Carvings?') },
  { key: 'omen', match: word('Omens?') },
  { key: 'shrouded', match: word('Shrouded') },
  { key: 'houseRule', match: /House Rules?/ },
  { key: 'trick', match: word('Tricks?') },
  { key: 'thrift', match: word('Thrift') },
  { key: 'interest', match: word('interest') },
  { key: 'favor', match: word('Favors?') },
  { key: 'warded', match: word('Warded') },
  { key: 'hasted', match: word('Hasted') },
  { key: 'kindled', match: word('Kindled') },
  { key: 'phased', match: word('Phased') },
  { key: 'gilded', match: word('Gilded') },
  { key: 'pinned', match: word('Pin(ned|s)?') },
  { key: 'exposed', match: word('Exposed') },
  { key: 'cursed', match: word('Cursed') },
  { key: 'smoldering', match: word('Smoldering') },
  { key: 'hollow', match: word('Hollow') },
  { key: 'heavy', match: word('Heavy') },
  { key: 'inspired', match: word('Inspired') },
  { key: 'reserve', match: word('reserves?') },
  { key: 'broker', match: word('Broker') },
];

export interface GlossaryEntry {
  readonly term: string;
  readonly text: string;
}

/** The terms a text uses, each with its explanation, in the order of the list above. */
export function glossaryFor(text: string, skip: readonly string[] = []): GlossaryEntry[] {
  const out: GlossaryEntry[] = [];
  for (const term of TERMS) {
    if (skip.includes(term.key) || !term.match.test(text)) continue;
    out.push({
      term: t(`gloss.${term.key}` as MessageKey),
      text: t(`gloss.${term.key}.text` as MessageKey),
    });
  }
  return out;
}

/** Every term, for the guide's glossary page. */
export function fullGlossary(): GlossaryEntry[] {
  return TERMS.map((term) => ({
    term: t(`gloss.${term.key}` as MessageKey),
    text: t(`gloss.${term.key}.text` as MessageKey),
  }));
}
